import * as XLSX from 'xlsx';

/**
 * Parsea un archivo Excel (.xlsx/.xls) y extrae filas de trabajadores.
 * Espera 15 columnas en orden (segun lo definido en la especificacion).
 * Devuelve un arreglo de filas con nombres de campos normalizados.
 *
 * @param {ArrayBuffer} buffer - Contenido del archivo como ArrayBuffer
 * @returns {Array} Filas parseadas
 */
export const parseExcelFile = (buffer) => {
    const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });

    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
        throw new Error('El archivo no contiene hojas de cálculo.');
    }

    const worksheet = workbook.Sheets[sheetName];
    // Obtener datos crudos como arreglo de arreglos (cada fila es un arreglo de celdas)
    const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

    if (rawData.length <= 1) {
        throw new Error('El archivo no contiene datos (solo cabecera o vacío).');
    }

    const rows = [];

    for (let i = 1; i < rawData.length; i++) {
        const cells = rawData[i];

        const hasData = cells.some((v) => v !== null && v !== undefined && v !== '');
        if (!hasData) continue;

        const parsedRow = {
            rowNumber: i + 1,
            cuil: parseCellValue(cells[0]),
            apellidoNombre: parseCellValue(cells[1]),
            mu: parseCellValue(cells[2]),
            fechaIngreso: parseDateValue(cells[3]),
            sector: parseCellValue(cells[4]),
            puesto: parseCellValue(cells[5]),
            sustancia: parseCellValue(cells[6]),
            fechaInicioExposicion: parseDateValue(cells[7]),
            fechaFinExposicion: parseDateValue(cells[8]),
            permanente: parseBitValue(cells[9], null),
            preocupacional: parseBitValue(cells[10], null),
            periodico: parseBitValue(cells[11], null),
            previoTarea: parseBitValue(cells[12], null),
            ausenciaProlongada: parseBitValue(cells[13], null),
            egreso: parseBitValue(cells[14], null),
        };

        rows.push(parsedRow);
    }

    return rows;
};

/**
 * Parsea el valor de una celda a string
 */
const parseCellValue = (value) => {
    if (value === null || value === undefined || value === '') return '';
    if (value instanceof Date) return String(value); // Las fechas se procesan en parseDateValue
    return String(value).trim();
};

/**
 * Parsea un valor de fecha. Soporta objetos Date y strings DD/MM/YYYY.
 */
const parseDateValue = (value) => {
    if (!value) return null;

    // Ya es un objeto Date (xlsx con cellDates: true)
    if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : value;
    }

    const str = String(value).trim();
    if (!str) return null;

    // Intentar formato DD/MM/YYYY
    const ddmmyyyy = str.match(/^(\d{1,2})[/\-.]+(\d{1,2})[/\-.]+(\d{4})$/);
    if (ddmmyyyy) {
        const [, day, month, year] = ddmmyyyy;
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return isNaN(date.getTime()) ? null : date;
    }

    return null;
};

/**
 * Parsea un valor binario (0 o 1), con fallback por defecto
 */
const parseBitValue = (value, defaultVal) => {
    if (value === null || value === undefined || value === '') return defaultVal;
    const num = parseInt(String(value).trim());
    if (num === 0 || num === 1) return num;
    return defaultVal;
};
